import type { ChannelHealthWire } from './types';

export type ChannelState =
  | 'connected'
  | 'disabled'
  | 'refusing'
  | 'never-received'
  | 'no-credential'
  | 'unknown';

export interface ChannelDiagnosis {
  state: ChannelState;
  /** Rótulo curto da pílula. */
  label: string;
  /** A frase que explica o estado — vem ANTES da tabela de fatos, na tela. */
  sentence: string | null;
  tone: 'ok' | 'warn' | 'bad' | 'mute';
}

const RECUSA: Record<string, string> = {
  invalid_signature:
    'rejeitada por assinatura inválida: o segredo do app configurado aqui não é o mesmo que o provedor usa para assinar',
  no_tenant:
    'rejeitada porque o identificador do canal não corresponde a nenhuma configuração daqui',
  invalid_payload: 'descartada porque o formato da entrega não foi reconhecido',
  handler_error: 'recebida, mas o processamento falhou depois',
  duplicate: 'ignorada por ser repetição de uma entrega já processada',
  channel_inactive: 'ignorada porque o canal estava desligado no momento em que ela chegou',
};

/**
 * Traduz a saúde de um canal em **uma frase** e um estado.
 *
 * **Por que é função pura, fora do componente.** O diagnóstico é a informação mais
 * importante da tela — quem a abre já tem um problema e precisa da frase, não da tabela.
 * Uma função pura é testável caso a caso, e cada estado abaixo corresponde a uma situação
 * real que já aconteceu neste projeto.
 *
 * **A ordem das perguntas importa.** «Está recusando?» vem antes de «tem credencial?»,
 * porque um canal pode ter credencial presente e estar recusando tudo — foi exatamente o
 * estado de 15/Set/2026, e perguntar pela presença primeiro o classificaria como saudável.
 */
export function diagnose(h: ChannelHealthWire): ChannelDiagnosis {
  // 0. Desligado vem ANTES de tudo, inclusive da recusa. Um canal desligado que recusa
  //    entregas está fazendo exatamente o que mandaram: é decisão, não defeito. Mostrar
  //    «Recusando» aqui mandaria a pessoa investigar o que ela mesma escolheu.
  if (!h.enabled) {
    return {
      state: 'disabled',
      label: 'Desligado',
      tone: 'mute',
      sentence:
        'Este canal está **desligado**: não recebe mensagens do cliente nem envia. Nada aqui está quebrado — é o interruptor.',
    };
  }

  const ultimaFoiRecusa = h.lastDeliveryOutcome !== null && h.lastDeliveryOutcome !== 'recognized';

  // 1. Está chegando coisa e sendo recusada? É o pior estado, e o mais invisível:
  //    do lado de fora parece que o cliente não escreveu.
  if (ultimaFoiRecusa) {
    const motivo = RECUSA[h.lastDeliveryOutcome as string] ?? 'rejeitada';
    return {
      state: 'refusing',
      label: 'Recusando entregas',
      tone: 'bad',
      sentence: `O provedor **está** entregando — e nós estamos recusando. A última entrega foi ${motivo}.`,
    };
  }

  // 2. Sem credencial é ausência, não defeito: a ação é colocar uma.
  if (!h.credentialPresent) {
    return {
      state: 'no-credential',
      label: 'Sem credencial',
      tone: 'mute',
      sentence:
        'Este canal não tem credencial guardada, então não consegue falar com o provedor nem receber dele.',
    };
  }

  // 3. Nunca recebeu é ambíguo POR NATUREZA — canal novo ou webhook não apontado. A tela
  //    diz as duas hipóteses em vez de escolher uma, e o teste de conexão desempata.
  if (h.lastInboundAt === null) {
    const testePassou = h.lastTestResult === 'ok';
    return {
      state: 'never-received',
      label: 'Nunca recebeu',
      tone: 'warn',
      sentence: testePassou
        ? 'Nenhuma entrega chegou até agora, mas o teste de conexão passou — a credencial está certa, então o que provavelmente falta é o webhook apontado para cá no painel do provedor.'
        : 'Nenhuma entrega chegou até agora. Pode ser um canal recém-configurado, ou o webhook pode não estar apontado para cá. Rode o teste de conexão para desempatar.',
    };
  }

  if (h.connected) {
    return { state: 'connected', label: 'Conectado', tone: 'ok', sentence: null };
  }

  // 4. Recebeu, tem credencial, e o teste não passou (ou nunca rodou). Não dá para
  //    afirmar mais do que isso — e afirmar a mais é o que produz diagnóstico errado.
  return {
    state: 'unknown',
    label: h.lastTestResult === null ? 'Nunca testado' : 'Atenção',
    tone: 'warn',
    sentence:
      h.lastTestResult === null
        ? 'As entregas estão chegando, mas a conexão com o provedor nunca foi testada daqui.'
        : 'As entregas estão chegando, mas o último teste de conexão não passou.',
  };
}

/** Frase do último teste, na língua de quem lê a tela. */
export function describeTest(h: ChannelHealthWire): string {
  switch (h.lastTestResult) {
    case 'ok':
      return 'conexão ok';
    case 'invalid_credential':
      return 'credencial recusada';
    case 'invalid_config':
      return 'provedor recusou o pedido — confira o identificador';
    case 'unreachable':
      return 'não foi possível falar com o provedor';
    case 'not_configured':
      return 'sem credencial para testar';
    default:
      return 'nunca testado';
  }
}
